import { ModuleLabel } from '../module-label';
import { AppToastOutlet, useAppToastRef } from '../../providers/app-toast-provider';
import '../../assets/entity-list.css';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ExplainedButton as Button } from '../explained-button';
import { Dialog } from '../dialogs/dialog';

export interface UserAssignment { key: string; assigned_at: string }
const emptyAssignments: UserAssignment[] = [];
const createFilters = (): DataTableFilterMeta => ({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ...Object.fromEntries(['name', 'assigned_at'].map(field => [field, {
        operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    }])),
});

export function UserAssignmentTable({ kind, items, assignments, loading, error, onSave, additionalColumns }: {
    kind: 'course' | 'module'; items: { key: string; name: string; managed?: boolean }[]; assignments?: UserAssignment[];
    loading: boolean; error?: string | null; onSave: (keys: string[]) => Promise<UserAssignment[]>;
    additionalColumns?: { header: string; body: (row: UserAssignment) => ReactNode }[];
}) {
    const title = kind === 'course' ? 'Course' : 'Module';
    const [assigned, setAssigned] = useState(assignments ?? emptyAssignments);
    const [selected, setSelected] = useState(assignments ?? emptyAssignments);
    const [filters, setFilters] = useState<DataTableFilterMeta>(createFilters);
    const [search, setSearch] = useState('');
    const [visibleRows, setVisibleRows] = useState<UserAssignment[] | null>(null);
    const [diffVisible, setDiffVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const pending = useRef(false);
    const mounted = useRef(true);
    const toast = useAppToastRef();
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        setAssigned(assignments ?? emptyAssignments); setSelected(assignments ?? emptyAssignments); setSaveError('');
    }, [assignments]);
    const rows = useMemo(() => {
        const baseline = new Map(assigned.map(item => [item.key, item.assigned_at]));
        return items.map(item => ({ ...item, assigned_at: baseline.get(item.key) ?? '' }));
    }, [items, assigned]);
    const visible = visibleRows ?? rows;
    const selectedKeys = new Set(selected.map(item => item.key));
    const assignedKeys = new Set(assigned.map(item => item.key));
    const added = selected.filter(item => !assignedKeys.has(item.key));
    const removed = assigned.filter(item => !selectedKeys.has(item.key));
    const busy = loading || saving || !assignments;
    const busyReason = saving ? 'Wait for the assignment changes to finish saving.' : 'Wait for the assignments to finish loading.';
    const updateVisible = (action: 'all' | 'none' | 'invert') => {
        setSelected(previous => {
            const next = new Map(previous.map(item => [item.key, item]));
            for (const item of visible) {
                if (action === 'none' || (action === 'invert' && next.has(item.key))) next.delete(item.key);
                else next.set(item.key, item);
            }
            return [...next.values()];
        });
    };
    const apply = async () => {
        if (pending.current || busy || error) return;
        pending.current = true; setSaving(true); setSaveError('');
        try {
            const saved = await onSave(selected.map(item => item.key));
            if (mounted.current) {
                setAssigned(saved); setSelected(saved);
                toast.current?.show({ severity: 'success', summary: 'Assignments updated', detail: `${title} assignments have been saved`, life: 2500 });
            }
        } catch (failure: any) {
            if (mounted.current) setSaveError(failure?.message || 'Could not save assignments. Your selections are unchanged.');
        } finally {
            pending.current = false;
            if (mounted.current) setSaving(false);
        }
    };
    if (error) return <p role="alert">The assignments list is unavailable. Please try again.</p>;
    return <div className="entity-list-container">
        <p className="m-0">A user can be assigned to a course or an individual module. See the {kind === 'course' ? 'Modules' : 'Courses'} tab.</p>
        <AppToastOutlet toastRef={toast} />
        <div className="entity-list-toolbar admin-toolbar">
            <Button label="All visible" text size="small" disabled={busy} disabledReason={busyReason} title={`Select ${kind}s matching the current filters`} onClick={() => updateVisible('all')} />
            <Button label="None visible" text size="small" disabled={busy} disabledReason={busyReason} title="Clear selection only for matching rows" onClick={() => updateVisible('none')} />
            <Button label="Invert visible" text size="small" disabled={busy} disabledReason={busyReason} title="Invert selection only for matching rows" onClick={() => updateVisible('invert')} />
            {!!(added.length || removed.length) && <Button label="View changes" icon="pi pi-list" text size="small" disabled={busy} disabledReason={busyReason} onClick={() => setDiffVisible(true)} />}
        </div>
        <div className="admin-toolbar mb-2">
            <InputText aria-label={`Filter ${kind}s by name`} placeholder={`Filter ${kind}s by name`} value={search} disabled={saving}
                style={{ width: '18rem', maxWidth: '100%' }} onChange={event => {
                    setSearch(event.target.value);
                    setFilters(previous => ({ ...previous, global: { value: event.target.value || null, matchMode: FilterMatchMode.CONTAINS } }));
                }} />
            <Button label="Clear filters" text disabled={saving} disabledReason={busyReason} onClick={() => { setSearch(''); setFilters(createFilters()); }} />
            <small>{selected.length} selected · {visible.length} shown</small>
        </div>
        {saveError && <p role="alert">{saveError}</p>}
        <div className="entity-list-wrapper">
            {/* Extra column bodies can change when metadata loads without changing assignment rows. */}
            <DataTable value={rows} dataKey="key" selection={selected} selectionMode="checkbox" cellSelection={false}
                cellMemo={!additionalColumns?.length}
                filters={filters} onFilter={event => setFilters(event.filters)} filterDisplay="menu" globalFilterFields={['name']}
                sortField="name" sortOrder={1} loading={busy} scrollable scrollHeight="flex" style={{ height: '100%' }}
                onValueChange={value => setVisibleRows(previous => previous?.length === value.length && previous.every((item, index) => item === value[index]) ? previous : value)}
                selectAll={visible.length > 0 && visible.every(item => selectedKeys.has(item.key))}
                onSelectAllChange={event => { if (!busy) updateVisible(event.checked ? 'all' : 'none'); }}
                onSelectionChange={event => {
                    if (busy) return;
                    const visibleKeys = new Set(visible.map(item => item.key));
                    setSelected(previous => [...previous.filter(item => !visibleKeys.has(item.key)), ...event.value.filter(item => visibleKeys.has(item.key))]);
                }}
                emptyMessage={items.length ? `No ${kind}s match these filters.` : `The ${kind} list is empty.`}>
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                <Column field="name" header={title} body={(row: { name: string; managed?: boolean }) => kind === 'module' ? <ModuleLabel name={row.name} managed={row.managed} /> : row.name} sortable filter showFilterMatchModes={false} filterPlaceholder={`Filter ${kind}`} />
                <Column field="assigned_at" header="Assigned at" sortable filter showFilterMatchModes={false} filterPlaceholder="YYYY-MM-DD" />
                {additionalColumns?.map(column => <Column key={column.header} header={column.header} body={column.body} />)}
            </DataTable>
        </div>
        <div className="admin-toolbar flex justify-content-end mt-3 flex-shrink-0">
            <Button label="Apply" icon="pi pi-check" disabled={busy || !(added.length || removed.length)}
                disabledReason={busy ? busyReason : 'Change an assignment checkbox first. There are no pending assignment changes.'} onClick={() => void apply()} />
        </div>
        <Dialog header="Changes preview" visible={diffVisible} onHide={() => setDiffVisible(false)} style={{ width: '28rem', maxWidth: '95vw' }} modal>
            <h4>Added</h4>{!added.length && <p>None</p>}
            {added.map(item => <p key={item.key} className="text-green-600">+ {items.find(row => row.key === item.key)?.name ?? item.key}</p>)}
            <h4>Removed</h4>{!removed.length && <p>None</p>}
            {removed.map(item => <p key={item.key} className="text-red-600">− {items.find(row => row.key === item.key)?.name ?? item.key}</p>)}
        </Dialog>
    </div>;
}
